import { Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from "@mui/material";
import React from "react";

/** Stripe API uses "canceled"; tolerate common variants. */
export function isStripeSubscriptionStatusCanceled(status) {
    if (status == null || String(status).trim() === "") {
        return false;
    }
    const s = String(status).trim().toLowerCase();
    return s === "canceled" || s === "cancelled";
}

/**
 * True when Stripe still has a subscription that is not in a canceled state.
 * Offers "Cancel on Stripe" to align billing (not based on cancel_at_period_end).
 */
export function shouldOfferCancelOnStripeFromStatusResult(result) {
    const sub = result?.stripeSubscription;
    if (!sub || typeof sub !== "object" || Object.keys(sub).length === 0) {
        return false;
    }
    if (sub.status == null || String(sub.status).trim() === "") {
        return false;
    }
    return !isStripeSubscriptionStatusCanceled(sub.status);
}

/**
 * Dialog showing /v1/stripe/status-check JSON and optional Cancel on Stripe when
 * stripeSubscription.status is not canceled.
 */
export default function StripeStatusCheckDialog({
    open,
    onClose,
    result,
    stripeCanceling,
    onRequestCancelOnStripe,
}) {
    const showCancelOnStripe = shouldOfferCancelOnStripeFromStatusResult(result);
    const sub = result?.stripeSubscription;
    const subStatus = sub?.status;
    const showCanceledInfo =
        sub &&
        typeof sub === "object" &&
        Object.keys(sub).length > 0 &&
        subStatus != null &&
        String(subStatus).trim() !== "" &&
        isStripeSubscriptionStatusCanceled(subStatus);

    const handleClose = () => {
        onClose();
    };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth scroll="paper">
            <DialogTitle>Stripe status check</DialogTitle>
            <DialogContent dividers>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Response from <code>/v1/stripe/status-check</code>
                </Typography>
                <Box
                    component="pre"
                    sx={{
                        m: 0,
                        p: 2,
                        borderRadius: 1,
                        bgcolor: "action.hover",
                        fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
                        fontSize: "0.8125rem",
                        lineHeight: 1.5,
                        overflow: "auto",
                        maxHeight: "min(50vh, 400px)",
                        whiteSpace: "pre-wrap",
                        wordBreak: "break-word",
                    }}
                >
                    {result == null ? "" : JSON.stringify(result, null, 2)}
                </Box>
                {showCanceledInfo && (
                    <Alert severity="info" sx={{ mt: 2 }}>
                        This subscription is canceled on Stripe.
                    </Alert>
                )}
                {showCancelOnStripe && (
                    <Alert severity="warning" sx={{ mt: 2 }}>
                        <Typography variant="body2" component="span" sx={{ display: "block", mb: 1 }}>
                            User canceled the subscription but Stripe isn&apos;t. Cancel the service on
                            Stripe.
                        </Typography>
                        <Button
                            variant="contained"
                            color="warning"
                            size="small"
                            disabled={stripeCanceling}
                            onClick={onRequestCancelOnStripe}
                        >
                            {stripeCanceling ? "Canceling…" : "Cancel on Stripe"}
                        </Button>
                    </Alert>
                )}
            </DialogContent>
            <DialogActions sx={{ px: 3, py: 2, flexWrap: "wrap", gap: 1 }}>
                <Button variant="contained" onClick={handleClose}>
                    Close
                </Button>
            </DialogActions>
        </Dialog>
    );
}
